import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from 'next-themes';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from 'sonner';
import PullToRefresh from './components/PullToRefresh';
import ShopPage from './pages/ShopPage';
import NotificationSound from './components/NotificationSound';
import './App.css';
import { initPushNotifications } from './utils/pushNotifications';

const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const CategoryPage = lazy(() => import('./pages/CategoryPage'));
const ProductDetailPage = lazy(() => import('./pages/ProductDetailPage'));
const ServicesManagement = lazy(() => import('./pages/ServicesManagement'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, initializing } = useAuth();

  const storedToken = typeof window !== 'undefined' && localStorage.getItem('access_token');
  
  if (initializing && !storedToken) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-[#2C5530] border-t-transparent rounded-full animate-spin" />
          <p className="text-[#5C665D] font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public Route Component (redirect to dashboard if already logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, initializing } = useAuth();

  if (initializing && isAuthenticated) {
    return null;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Suspense fallback={
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#2C5530] border-t-transparent rounded-full animate-spin" />
      </div>
    }>
      <Routes>
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <RegisterPage />
            </PublicRoute>
          }
        />
        <Route path="/" element={<ShopPage />} />
        <Route path="/shop" element={<ShopPage />} />
        <Route path="/product/:productId" element={<ProductDetailPage />} />
        <Route path="/category/:categoryId" element={<CategoryPage />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/services-management"
          element={
            <ProtectedRoute>
              <ServicesManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
// --- PUSH NOTIFICATIONS INIT ---
function PushHandler() {
  const { user, isAuthenticated } = useAuth();
  React.useEffect(() => {
    if (!isAuthenticated || !user) return undefined;

    let disposed = false;
    let removeListeners;
    const id = user._id || user.id || user.email;
    initPushNotifications(id).then((remove) => {
      if (disposed) {
        remove?.();
      } else {
        removeListeners = remove;
      }
    }).catch((error) => {
      console.error('Unable to initialize push notifications:', error);
    });

    return () => {
      disposed = true;
      removeListeners?.();
    };
  }, [isAuthenticated, user]);
  return null;
}
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false} storageKey="cashhub-theme">
          <NotificationSound />
          <PushHandler />
          <PullToRefresh>
            <AppRoutes />
          </PullToRefresh>
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
